import React, { useEffect, useMemo, useState } from 'react';
import * as firebase from 'firebase/app';
import * as auth from 'firebase/auth';
import * as firestore from 'firebase/firestore';
import { Container, createTheme, ThemeProvider, Box, LinearProgress, Typography, AppBar, Toolbar, Drawer, Tooltip, Avatar, IconButton, Menu, MenuItem, Divider, List, ListItem, ListItemButton, ListItemIcon, ListItemText, LinkProps, Breadcrumbs, Button, PaletteMode, ThemeOptions, Theme, alpha, Stack } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import { SignIn } from './SignIn';
import { EMSAppBar } from './components/EMSAppBar';
import { useAppDispatch, useAppSelector } from './store';
import { signIn } from './store/thunks/authThunks';
import { ArrowBackRounded, HomeRounded, InboxRounded, MailRounded } from '@mui/icons-material';
import { MyDrawer } from './components/EMSSideBar';
import { Link, Outlet, Route, Routes, useLocation, useNavigate, useOutletContext } from 'react-router-dom';
import LinkBehavior from './components/LinkBehavior';
import { EmployeesPage } from './components/employee/EmployeesPage';
import { CACHE_SIZE_UNLIMITED } from 'firebase/firestore';
import { AdminIndex } from './components/admin/Index';
import { HomePage } from './components/HomePage';
import { Index } from './components/Index';
import { EmployeePage, EmployeePageContext } from './components/employee/EmployeePage';
import { InfoBox } from './components/employee/InfoBox';
import { DocumentsBox } from './components/employee/DocumentsBox';
import { RequestsBox } from './components/employee/RequestsBox';
import { AttendanceHomePage } from './components/attendance/AttendanceHomePage';
import { ReceiptRequestsPage } from './components/receipts/ReceiptRequestsPage';
import { VacationRequestsPage } from './components/vacation/VacationRequestsPage';
import { VacationRequestPage } from './components/vacation/VacationRequestPage';
import { PassportRequestsPage } from './components/dashboard/passport/PassportRequestsPage';
import { MessagesHomePage } from './components/messages/MessagesHomePage';
import { StartConversationBox } from './components/messages/StartConversationBox';
import { ConversationBox } from './components/messages/ConversationBox';
import { EmployeesOnVacationPage } from './components/employee/EmployeesOnVacationPage';
import { NotificationPage } from './components/notifications/NotificationPage';
import { NotificationsPage } from './components/notifications/NotificationsPage';
import { UniformRequestsPage } from './components/dashboard/uniform/UniformRequestsPage';
import { UniformRequestPage } from './components/dashboard/uniform/UniformRequestPage';
import { NotificationsPersonalDocumentsPage } from './components/notifications/NotificationsPersonalDocumentsPage';
import { MedicalPaperRequestsPage } from './components/dashboard/medical/MedicalPaperRequestsPage';
import { MedicalPaperRequestPage } from './components/dashboard/medical/MedicalPaperRequestPage';
import { UmrahPaperRequestsPage } from './components/dashboard/umrah/UmrahPaperRequestsPage';
import { UmrahPaperRequestPage } from './components/dashboard/umrah/UmrahPaperRequestPage';
import { PassportRequestPage } from './components/dashboard/passport/PassportRequestPage';
import { ManageUsersPage } from './components/admin/ManageUsersPage';
import { SecureModule } from './security/SecureModule';
import Role, { ROLES, ROLES_GROUP } from './security/Role';
import Permission, { PERMISSIONS_ARTICLES, PERMISSIONS_USERS } from './security/Permission';
import { ManageUserPage } from './components/admin/ManageUserPage';
import { ArticleIndex } from './components/article/Index';
import { CreateArticle } from './components/article/CreateArticle';
import { EditArticle } from './components/article/EditArticle';
import { SalaryDeductionBox } from './components/employee/SalaryDeductionBox';

const defaultLoadState = { AuthLoading: true, };
type LoadType = keyof typeof defaultLoadState;

// Initialize database
const app = firebase.initializeApp({
  apiKey: "AIzaSyDNwaIqeLDjyh11wRuwIkgZ7XsFRlK_seM",
  authDomain: "nadafa-ems.firebaseapp.com",
  projectId: "nadafa-ems",
  storageBucket: "nadafa-ems.appspot.com",
  messagingSenderId: "929686863993",
  appId: "1:929686863993:web:cd741cfd58bd6abbdf94a8",
  measurementId: "G-134QLEXCL7",
});

const db = firestore.initializeFirestore(app, {
  cacheSizeBytes: CACHE_SIZE_UNLIMITED,
  ignoreUndefinedProperties: true,
});

firestore.enableIndexedDbPersistence(db)
  .catch((err) => {
      if (err.code == 'failed-precondition') {
          // Multiple tabs open, persistence can only be enabled
          // in one tab at a a time.
          // ...
      } else if (err.code == 'unimplemented') {
          // The current browser does not support all of the
          // features required to enable persistence
          // ...
      }
  });

const themeOptions: ThemeOptions = {
  components: {
    MuiCssBaseline: {
      styleOverrides: (theme) => ({
        body: {
          // scrollbarColor: "#6b6b6b #2b2b2b",
          "&::-webkit-scrollbar, & *::-webkit-scrollbar": {
            // backgroundColor: theme.palette.mode === 'dark' ? alpha('#ffffff', 0.5) : alpha('#000000', 0.5),
            borderRadius: 8,
            width: 10,
          },
          "&::-webkit-scrollbar-track, & *::-webkit-scrollbar-track": {
            backgroundColor: theme.palette.mode === 'dark' ? alpha('#ffffff', 0.05) : alpha('#000000', 0.15),
          },
          "&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb": {
            borderRadius: 8,
            backgroundColor: theme.palette.primary.dark, // "#6b6b6b",
            minHeight: 24,
            width: 8,
          },
          "&::-webkit-scrollbar-thumb:hover, & *::-webkit-scrollbar-thumb:hover": {
            backgroundColor: theme.palette.primary.main // "#959595",
          },
          "&::-webkit-scrollbar-corner, & *::-webkit-scrollbar-corner": {
            backgroundColor: "#2b2b2b",
          },
        },
      }),
    },
    MuiLink: {
      defaultProps: {
        component: LinkBehavior,
      } as LinkProps,
    },
    MuiButtonBase: {
      defaultProps: {
        LinkComponent: LinkBehavior,
      },
    },
    MuiListItemButton: {
      defaultProps: {
        LinkComponent: LinkBehavior,
      },
    },
    MuiTooltip: {
      styleOverrides: {
        popper: {
          zIndex: 19999,
        }
      }
    }
  },
};

const SplashScreen = (props: { theme: Theme }) => {
 const { theme } = props;
 return (
  <ThemeProvider theme={theme}>
    <Container component="main" maxWidth="xs">
      <CssBaseline />
      <Box height="100vh" display="flex" flexDirection="column" alignItems="center">
        <Box display="flex" flexDirection="column" flex={1} />
        <LinearProgress style={{ width: '100%' }} />
        <Box width="100%">
          <Typography variant="caption">Please wait...</Typography>
        </Box>
        <Box display="flex" flex={1} />
      </Box>
    </Container>
  </ThemeProvider>
 )
};

const Layout = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const navigateBackOnClick = () => {
    navigate(-1);
  };

  return (
    <SecureModule roles={ROLES}>
      <main>
        <CssBaseline />
        <MyDrawer />
        <Box ml={`${300}px`} pb={5}>
          <EMSAppBar />
          <Container maxWidth="lg" sx={{ pt: 1 }}>
            <Box mt={2} mb={4} display="flex" flexDirection="row">
              <IconButton onClick={() => navigate('/', { replace: true })}>
                <HomeRounded />
              </IconButton>
              <div style={{ flexGrow: 1 }} />
              <Button startIcon={<ArrowBackRounded />} onClick={navigateBackOnClick} disabled={location.pathname === '/'}>
                BACK
              </Button>
            </Box>
            <Outlet />
          </Container>
        </Box>
      </main>
    </SecureModule>
  );
};

const ProtectedRoute = (props: { roles?: Role[], permissions?: Permission[] }) => {
  const { roles, permissions } = props;
  const context = useOutletContext();
  return (
    <SecureModule roles={roles} permissions={permissions}>
      <Outlet context={context} />
    </SecureModule>
  )
};

function App() {
  const dispath = useAppDispatch();
  const isSignedIn = useAppSelector((s) => s.auth.isSignedIn);
  const mode = useAppSelector((s) => s.app.mode);

  const [loadState, setLoadState] = useState(defaultLoadState);
  const [loading, setLoading] = useState(true);

  const theme = useMemo(() => createTheme({
    palette: { mode },
    ...themeOptions,
  }), [mode]);

  useEffect(() => {
    if (!Object.values(loadState).every(v => !v)) return;
    setTimeout(() => setLoading(false), 1000);
  }, [loadState]);
  
  useEffect(() => {
    auth.onAuthStateChanged(auth.getAuth(app), (user) => {
      if (user) {
        dispath(signIn(user));
      }

      setLoadState({ ...loadState, AuthLoading: false });
    });
  }, []);

  if (loading) {
    return <SplashScreen theme={theme} />
  }

  if (!isSignedIn) {
    return (
      <ThemeProvider theme={theme}>
        <Container component="main" maxWidth="xs">
          <CssBaseline />
          <SignIn />
        </Container>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <Routes>
        <Route path="/" element={<Layout />}>
        <Route index element={<Index />} />

          <Route path="home">
            <Route index element={<Index />} />
            <Route path="employees">
              <Route index element={<EmployeesPage />} />
              <Route path="vacations" element={<EmployeesOnVacationPage />} />
              <Route path=":idNumber" element={<EmployeePage />}>
                <Route index element={<InfoBox />} />
                <Route path="info" element={<InfoBox />} />
                <Route path="documents" element={<DocumentsBox />} />
                <Route path="requests" element={<ProtectedRoute roles={[...ROLES_GROUP.SERVICES, ...ROLES_GROUP.DASHBOARD]} />}>
                  <Route index element={<RequestsBox />} />
                </Route>
                <Route path="salary-deduction" element={<SalaryDeductionBox />} />
              </Route>
            </Route>
            <Route path="attendance" element={<AttendanceHomePage />} />
            <Route path="notifications">
              <Route index element={<NotificationsPage />} />
              <Route path=":id" element={<NotificationPage />}></Route>
              <Route path="personalDocuments" element={<NotificationsPersonalDocumentsPage />}></Route>
            </Route>
          </Route>

          <Route path="services" element={<ProtectedRoute roles={ROLES_GROUP.SERVICES} />}>
            <Route index element={<Typography>Services Index</Typography>} />
            <Route path="vacationRequests">
              <Route index element={<VacationRequestsPage />} />
              <Route path=":id" element={<VacationRequestPage />} />
            </Route>
            <Route path="iqamaRenewalReceipt" element={<ReceiptRequestsPage type='Iqama' />} />
            <Route path="vacationReceipt" element={<ReceiptRequestsPage type='Vacation' />} />
            <Route path="otherFiles" element={<ReceiptRequestsPage type='Others' />} />
          </Route>
          
          <Route path="dashboard" element={<ProtectedRoute roles={ROLES_GROUP.DASHBOARD} />}>
            <Route index element={<Typography>Dashboard Index</Typography>} />
            <Route path="medicalRequests" element={<ProtectedRoute roles={ROLES_GROUP.MEDICAL} />}>
              <Route index element={<MedicalPaperRequestsPage />} />
              <Route path=":id" element={<MedicalPaperRequestPage />} />
            </Route>
            <Route path="umrahRequests" element={<ProtectedRoute roles={ROLES_GROUP.UMRAH} />}>
              <Route index element={<UmrahPaperRequestsPage /> } />
              <Route path=":id" element={<UmrahPaperRequestPage />} />
            </Route>
            <Route path="uniformRequests" element={<ProtectedRoute roles={ROLES_GROUP.UNIFORM} />}>
              <Route index element={<UniformRequestsPage />} />
              <Route path=":id" element={<UniformRequestPage />} />
            </Route>
            <Route path="passportRequests" element={<ProtectedRoute roles={ROLES_GROUP.PASSPORT} />}>
              <Route index element={<PassportRequestsPage />} />
              <Route path=":id" element={<PassportRequestPage />} />
            </Route>
          </Route>

          <Route element={<ProtectedRoute permissions={[...PERMISSIONS_USERS, ...PERMISSIONS_ARTICLES]} />}>
            <Route path="messages">
              <Route index element={<MessagesHomePage />} />
              <Route path="t" element={<MessagesHomePage />}>
                <Route path="start-conversation" element={<StartConversationBox />} />
                <Route path=":threadId" element={<ConversationBox />} />
              </Route>
            </Route>
            <Route path="admin">
              <Route index element={<AdminIndex />} />
              <Route path="manageUsers">
                <Route index element={<ManageUsersPage />} />
                <Route path=":loginId" element={<ManageUserPage />} />
              </Route>
              <Route path="articles">
                <Route index element={<ArticleIndex />} />
                <Route path='create' element={<CreateArticle />} />
                <Route path=":articleId" element={<EditArticle />} />
              </Route>
            </Route>
          </Route>
        </Route>
      </Routes>
    </ThemeProvider>
  );
}

export default App;
